import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Mail, KeyRound, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { validate } from "../lib/validate";
import { Field, inputProps, LogoMark } from "../components/site/Primitives";

const RequestForm = () => {
  const { t, lang } = useLang();
  const r = t.reset;
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [state, setState] = useState({ busy: false, sent: false, failed: null });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate({ email }, { email: ["required", "email"] });
    setError(errs.email);
    if (errs.email) return;
    setState({ busy: true, sent: false, failed: null });
    try {
      await api.post("/auth/forgot-password", { email, lang });
      setState({ busy: false, sent: true, failed: null });
    } catch (err) {
      setState({ busy: false, sent: false, failed: err?.response?.status === 429 ? r.tooMany : t.errors.generic });
    }
  };

  if (state.sent) return <p className="success-msg" role="status" data-testid="forgot-success"><CheckCircle2 aria-hidden="true" />{r.sent}</p>;
  return (
    <form onSubmit={submit} noValidate className="auth-form" data-testid="forgot-form">
      <p className="pro-intro" style={{ margin: 0 }}>{r.requestDesc}</p>
      <Field id="forgot-email" label={t.auth.email} error={error} required>
        <input type="email" autoComplete="email" {...inputProps("forgot-email", error, true)} value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} />
      </Field>
      {state.failed && <p className="error-banner" role="alert" style={{ marginTop: 0 }} data-testid="forgot-error"><AlertCircle aria-hidden="true" />{state.failed}</p>}
      <button type="submit" className="btn btn-primary" disabled={state.busy} data-testid="forgot-submit-btn">
        {state.busy ? <Loader2 className="spin" aria-hidden="true" /> : <Mail aria-hidden="true" />}{r.send}
      </button>
    </form>
  );
};

const NewPasswordForm = ({ token }) => {
  const { t } = useLang();
  const r = t.reset;
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const [values, setValues] = useState({ password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ busy: false, done: false, failed: null });

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values, { password: ["required", ["min", 8]], confirm: ["required"] });
    if (!errs.confirm && values.confirm !== values.password) errs.confirm = ["mismatch"];
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState({ busy: true, done: false, failed: null });
    try {
      await api.post("/auth/reset-password", { token, password: values.password });
      signOut();
      setState({ busy: false, done: true, failed: null });
    } catch (err) {
      setState({ busy: false, done: false, failed: err?.response?.status === 400 ? r.invalid : t.errors.generic });
    }
  };

  if (state.done) {
    return (
      <div data-testid="reset-success">
        <p className="success-msg" role="status" style={{ marginTop: 0 }}><CheckCircle2 aria-hidden="true" />{r.done}</p>
        <button type="button" className="btn btn-primary" style={{ marginTop: 18 }} onClick={() => navigate("/espace")} data-testid="reset-goto-login-btn">{t.auth.login}</button>
      </div>
    );
  }
  const set = (k) => (e) => { setValues((v) => ({ ...v, [k]: e.target.value })); setErrors((er) => ({ ...er, [k]: undefined })); };
  return (
    <form onSubmit={submit} noValidate className="auth-form" data-testid="reset-form">
      <Field id="reset-password" label={`${r.newPassword} — ${t.auth.passwordHint}`} error={errors.password} required>
        <input type="password" autoComplete="new-password" {...inputProps("reset-password", errors.password, true)} value={values.password} onChange={set("password")} />
      </Field>
      <Field id="reset-confirm" label={r.confirm} error={errors.confirm} required>
        <input type="password" autoComplete="new-password" {...inputProps("reset-confirm", errors.confirm, true)} value={values.confirm} onChange={set("confirm")} />
      </Field>
      {state.failed && <p className="error-banner" role="alert" style={{ marginTop: 0 }} data-testid="reset-error"><AlertCircle aria-hidden="true" />{state.failed}</p>}
      <button type="submit" className="btn btn-primary" disabled={state.busy} data-testid="reset-submit-btn">
        {state.busy ? <Loader2 className="spin" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}{r.save}
      </button>
    </form>
  );
};

export default function ResetPasswordPage() {
  const { t, lang, toggle } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token");
  return (
    <main className="login-wrap" data-testid="reset-password-page">
      <div className="panel login-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" className="logo"><LogoMark /> IndusHub</Link>
          <button type="button" className="btn btn-ghost lang-btn btn-sm" onClick={toggle} data-testid="reset-lang-toggle-btn">{lang === "fr" ? "EN" : "FR"}</button>
        </div>
        <h1>{token ? t.reset.titleNew : t.reset.titleRequest}</h1>
        <div style={{ marginTop: 18 }}>{token ? <NewPasswordForm token={token} /> : <RequestForm />}</div>
        <Link to="/espace" className="icon-link" style={{ marginTop: 22 }} data-testid="reset-back-link"><ArrowLeft aria-hidden="true" />{t.reset.back}</Link>
      </div>
    </main>
  );
}
